import React from 'react';
import { Helmet } from 'react-helmet-async';
// material-ui
import {
    Alert,
    Box,
    Card,
    CircularProgress,
    Container,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Toolbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
// components
import EditorCells from './editorCells';
import { GridListHead } from '../../../sections/grid';
import Scrollbar from '../../../components/scrollbar/Scrollbar';
// helpers
import i18n from '../../../i18';
import helper from '../../../utils/helper';
import { formatError } from '../../../utils/formatError';
import config from '../../../config';
import { DialogSaveAdapter } from '../../../adapters/DialogSaveAdapter';
import { FilterField } from './editors/components/FilterField';
import { CustomTablePagination } from '../../../components/pagination/CustomTablePagination';
//-----------------------------------------------
const StyledRoot = styled(Toolbar)(({ theme }) => ({
    height: 96,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1, 0, 3)
}));
//-----------------------------------------------
export default class PermissionEditorAbstract extends React.Component {
    Name = '';
    Server = null;
    Head = [];
    List = [];

    saveCurrentData = () => {};
    customLogicOnMount = () => {};
    constructor(props) {
        super(props);

        this.Head = [];

        this.state = {
            page: 0,
            rowsPerPage: 25,
            orderBy: 'id',
            order: 'asc',
            totalCount: 0,
            sortConfig: config.defaultSort,
            currentValues: {},

            filterRequest: [
                [
                    {
                        field: this.props.filterBy,
                        op: '=',
                        value:
                            this.props.filterItemId ||
                            this.props.filterItems?.[0]?.id
                    }
                ]
            ],

            filterItems: this.props.filterItems || [],
            filterItemId:
                this.props.filterItemId ||
                this.props.filterItems?.[0]?.id ||
                null,
            filterCount: 0,

            snackbarOpen: false,
            alertText: '',
        };
    }

    async componentDidMount() {
        this.state.filterItemId
            ? await this.retrieveData(this.saveCurrentData.bind(this))
            : this.setState({ alertText: i18n.t('errors.chooseRole'), snackbarOpen: true });
    }

    async retrieveData(setDataCallback, filter = this.state.filterRequest) {
        try {
            const sort = this.Server.getSortField(
                this.state.sortConfig.field,
                this.state.sortConfig.direction || 'desc'
            );

            let result = await this.Server.getAllFiltered(filter, sort, {
                paging: {
                    page: this.state.page,
                    pageSize: this.state.rowsPerPage
                }
            });

            this.List = result.data.filter(
                (item, index, self) =>
                    index === self.findIndex((t) => t.id === item.id)
            );

            if (typeof setDataCallback === 'function') setDataCallback();

            if (this.state.page === 0) {
                this.setState({ totalCount: result.total_count });
            }
        } catch (error) {
            this.setState({
                snackbarOpen: true,
                alertText: formatError(error)
            });
        } finally {
            this.setState({ isLoading: false });
        }
    }
    async updatePermissionsData() {
        const data = await this.props.context
            .getState()
            .server.users.metaInfo();
        this.props.context.update({
            permissions: data.permissions,
            actions: data.actionPermissions
        });
    }

    handleChangeCell = (event, permissionId, cellId) => {
        const { value } = event.target;
        this.setState((prevState) => ({
            currentValues: {
                ...prevState.currentValues,
                [permissionId]: {
                    ...prevState.currentValues[permissionId],
                    [cellId]: value
                }
            }
        }));
    };

    handleSaveCell = async (id) => {
        const data = this.List.find((item) => item.id === id);
        const adapter = new DialogSaveAdapter(data);

        const adaptedData = adapter.newData;
        let value = this.state.currentValues[id];
        const newData = { ...adaptedData, ...value };

        if (Object.keys(data).length) {
            await this.Server.update(id, newData);
        }

        this.retrieveData(this.saveCurrentData.bind(this)).then(() =>
            this.updatePermissionsData()
        );
    };

    handleFilterItems = async (filterId) => {
        const filterRequest = [
            [
                {
                    field: this.props.filterBy,
                    op: '=',
                    value: filterId
                }
            ]
        ];
        this.setState({ filterRequest, filterItemId: filterId }, () =>
            this.retrieveData(this.saveCurrentData.bind(this))
        );
    };

    handleRequestSort = (sortField) => {
        const { field, direction } = this.state.sortConfig;
        const isAsc = field === sortField && direction === 'asc';
        this.setState(
            {
                sortConfig: {
                    field: sortField,
                    direction: isAsc ? 'desc' : 'asc'
                }
            },
            () => this.retrieveData(this.saveCurrentData.bind(this))
        );
    };

    handleChangePage = (event, newPage) => {
        this.setState({ page: newPage }, () =>
            this.retrieveData(this.saveCurrentData.bind(this))
        );
    };

    handleChangeRowsPerPage = (event) => {
        this.setState(
            { page: 0, rowsPerPage: parseInt(event.target.value, 10) },
            () => this.retrieveData(this.saveCurrentData.bind(this))
        );
    };

    getValue = (row, field) => {
        if (field.object) {
            return row[field.object];
        }
        return row[field.id];
    };

    renderGridSingleRow(row) {
        const { id } = row;
        const tableFields = this.Head;

        return (
            <TableRow hover key={id} tabIndex={-1}>
                {tableFields.map((field) => {
                    let Cell = EditorCells[field.cell];
                    if (!Cell) Cell = EditorCells.Text;
                    let value = this.getValue(row, field);
                    return (
                        <Cell
                            key={field.id}
                            id={row.id}
                            row={row}
                            value={value}
                            field={field}
                            handleChange={this.handleChangeCell}
                            handleSave={this.handleSaveCell}
                            currentValues={this.state.currentValues}
                        />
                    );
                })}
            </TableRow>
        );
    }
    renderGridBodyRows() {
        return (
            <>
                {this.List.map((row) => {
                    return this.renderGridSingleRow(row);
                })}
            </>
        );
    }

    renderGridBodyEmptyRows() {
        const emptyRows =
            this.state.page > 0
                ? Math.max(
                    0,
                    (1 + this.state.page) * this.state.rowsPerPage -
                          this.state.totalCount
                )
                : 0;
        return (
            <>
                {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                    </TableRow>
                )}
            </>
        );
    }

    renderLoading() {
        return (
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 2
                }}
            >
                <CircularProgress />
            </Box>
        );
    }
    render() {
        const { order, orderBy } = this.state;
        const headLabel = this.Head;
        const createSortHandler = (property) => () => {
            this.handleRequestSort(property);
        };

        return (
            <>
                <Helmet>
                    <title>
                        {helper.formPageTitle(`pages.${this.Name}.title`)}
                    </title>
                </Helmet>

                <Container maxWidth={false}>
                    <Card>
                        <StyledRoot>
                            <FilterField
                                items={this.state.filterItems}
                                selectedItem={this.state.filterItemId}
                                changeValue={this.handleFilterItems}
                            />
                        </StyledRoot>

                        <Scrollbar>
                            {this.state.isLoading && this.renderLoading()}
                            <TableContainer
                                sx={{ overflowX: 'initial', minHeight: 53 * 6 }}
                            >
                                <Table stickyHeader>
                                    <GridListHead
                                        headLabel={headLabel}
                                        checkboxCell={false}
                                        showMenu={false}
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={createSortHandler}
                                    />
                                    <TableBody>
                                        {this.state.filterRequest &&
                                            this.renderGridBodyRows()}
                                        {this.renderGridBodyEmptyRows()}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Scrollbar>
                        <CustomTablePagination
                            component="div"
                            count={this.state.totalCount}
                            rowsPerPage={this.state.rowsPerPage}
                            page={this.state.page}
                            onPageChange={this.handleChangePage}
                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                        />
                    </Card>
                </Container>

                <Snackbar
                    open={this.state.snackbarOpen}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    onClose={() => this.handleSnackbarClose()}
                >
                    <Alert
                        onClose={() => this.handleSnackbarClose()}
                        severity="error"
                        sx={{ width: '100%' }}
                    >
                        {this.state.alertText}
                    </Alert>
                </Snackbar>
            </>
        );
    }
    handleSnackbarClose() {
        this.setState({ snackbarOpen: false });
    }
}
